import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Label } from '../components/ui/Label';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  ApiError,
  type FormConfig,
  type FormStatRow,
  type FormStatus,
  type FormTrigger,
  type FormType,
  type MailingList,
  type SignupForm,
  type SignupFormInput,
  type SignupFormsResponse,
} from '../types/api';

const TYPES: { value: FormType; label: string; hint: string }[] = [
  { value: 'popup', label: 'Popup', hint: 'A box in the middle of the page.' },
  { value: 'flyout', label: 'Flyout', hint: 'A small box in the corner.' },
  { value: 'fullscreen', label: 'Full screen', hint: 'Covers the whole page.' },
  { value: 'embedded', label: 'Embedded', hint: 'Sits where you place the div on a page.' },
  { value: 'bar', label: 'Sticky bar', hint: 'A bar at the top of the page.' },
];

const TRIGGERS: { value: FormTrigger; label: string }[] = [
  { value: 'immediate', label: 'At once' },
  { value: 'delay', label: 'After a delay' },
  { value: 'scroll', label: 'After scrolling' },
  { value: 'exit_intent', label: 'Exit intent (desktop only)' },
];

const STATUS_LABEL: Record<FormStatus, string> = { draft: 'Draft', live: 'Live', off: 'Off' };

const NEW_CONFIG: FormConfig = {
  texts: {
    headline: 'Join NoPain',
    body: 'Get tips for before and after your tattoo, news and offers straight to your inbox.',
    buttonText: 'Sign up',
    successText: 'Thank you! Check your inbox to confirm your signup.',
    nextText: 'Next',
    step2Headline: 'Tell us a little more',
    step2Body: 'Optional. It helps us send you what fits.',
    skipText: 'Skip',
    birthdayText: 'Yes, send me an offer on my birthday.',
  },
  translations: {},
  imageUrl: null,
  couponCode: null,
  design: {
    backgroundColor: '#ffffff',
    textColor: '#243a53',
    buttonColor: '#208233',
    cornerRadius: 12,
    headingFont: 'poppins',
    dimBackground: true,
  },
  targeting: {
    trigger: 'delay',
    delaySeconds: 10,
    scrollPercent: 40,
    includePaths: [],
    excludePaths: [],
    countries: [],
    languages: [],
    visitor: 'all',
    showOnMobile: true,
    showOnDesktop: true,
    frequencyDays: 14,
  },
  steps: { multiStep: false, askName: false, askBirthday: false },
  ab: {
    enabled: false,
    splitPercent: 50,
    winner: null,
    variantB: { texts: {}, translations: {}, design: {} },
  },
};

const NEW_FORM: SignupFormInput = {
  name: '',
  type: 'popup',
  listId: null,
  countViews: false,
  config: NEW_CONFIG,
};

const HEX = /^#[0-9a-fA-F]{6}$/;
const PATH = /^\/[^\s?#]*$/;

function lines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

function codes(text: string, upper: boolean): string[] {
  return text
    .split(/[\s,]+/)
    .map((c) => (upper ? c.toUpperCase() : c.toLowerCase()))
    .filter(Boolean);
}

function validate(input: SignupFormInput): string | null {
  const c = input.config;
  if (input.name.trim() === '') return 'Enter a name.';
  if (c.texts.headline.trim() === '') return 'Enter a headline.';
  if (c.texts.body.trim() === '') return 'Enter a text.';
  if (c.texts.buttonText.trim() === '') return 'Enter the button text.';
  if (
    ![c.design.backgroundColor, c.design.textColor, c.design.buttonColor].every((h) => HEX.test(h))
  ) {
    return 'Colours must look like #208233.';
  }
  const t = c.targeting;
  if (t.delaySeconds < 1 || t.delaySeconds > 300) return 'The delay must be 1 to 300 seconds.';
  if (t.scrollPercent < 5 || t.scrollPercent > 100) return 'The scroll depth must be 5 to 100 %.';
  if (t.frequencyDays < 1 || t.frequencyDays > 365) return 'The frequency must be 1 to 365 days.';
  if (![...t.includePaths, ...t.excludePaths].every((p) => PATH.test(p))) {
    return 'A path starts with / and can end with * (for example /blog/*).';
  }
  if (!t.countries.every((x) => /^[A-Z]{2}$/.test(x)))
    return 'A country is a two-letter code like DK.';
  if (!t.languages.every((x) => /^[a-z]{2}$/.test(x)))
    return 'A language is a two-letter code like da.';
  if (c.ab.splitPercent < 1 || c.ab.splitPercent > 99) return 'The split must be 1 to 99 %.';
  return null;
}

function rate(row: { views: number; signups: number } | undefined): string {
  if (!row || row.views === 0) return '-';
  return `${((row.signups / row.views) * 100).toFixed(1)} %`;
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

function Check({
  id,
  label,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

const selectClass = 'h-[42px] w-full rounded-[9px] border border-zinc-300 bg-card px-3 text-sm';

function Editor({
  initial,
  form,
  lists,
  readOnly,
  onClose,
}: {
  initial: SignupFormInput;
  form: SignupForm | null;
  lists: MailingList[];
  readOnly: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SignupFormInput>(initial);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (input: SignupFormInput) =>
      apiFetch<SignupForm>(form ? `/forms/${form.id}` : '/forms', {
        method: form ? 'PUT' : 'POST',
        body: input,
      }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      if (!form) onClose();
    },
  });

  const problem = validate(draft);
  const c = draft.config;
  const t = c.targeting;
  const error =
    save.error instanceof ApiError
      ? (save.error.body.message ?? 'Could not be saved. Try again.')
      : save.isError
        ? 'Could not be saved. Try again.'
        : null;

  function edit(next: Partial<SignupFormInput>) {
    save.reset();
    setSaved(false);
    setDraft((d) => ({ ...d, ...next }));
  }
  function config(next: Partial<FormConfig>) {
    edit({ config: { ...c, ...next } });
  }
  function targeting(next: Partial<FormConfig['targeting']>) {
    config({ targeting: { ...t, ...next } });
  }
  function text(key: keyof FormConfig['texts'], value: string) {
    config({ texts: { ...c.texts, [key]: value } });
  }
  function number(value: string): number {
    return value === '' ? Number.NaN : Number(value);
  }

  const publicLists = lists.filter((l) => !l.archived && l.isPublic && !l.isDefaultSignup);

  return (
    <Card className="space-y-6 p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{form ? `Edit ${form.name}` : 'New form'}</h2>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
      {!form ? (
        <p className="text-sm text-muted">
          A new form is saved as a draft. It does not show until you set it live.
        </p>
      ) : null}

      <fieldset disabled={readOnly} className="grid gap-4 md:grid-cols-2">
        <Field id="form-name" label="Name">
          <Input
            id="form-name"
            value={draft.name}
            onChange={(e) => edit({ name: e.target.value })}
          />
        </Field>
        <Field id="form-type" label="Type" hint={TYPES.find((x) => x.value === draft.type)?.hint}>
          <select
            id="form-type"
            className={selectClass}
            value={draft.type}
            onChange={(e) => edit({ type: e.target.value as FormType })}
          >
            {TYPES.map((x) => (
              <option key={x.value} value={x.value}>
                {x.label}
              </option>
            ))}
          </select>
        </Field>
        <Field id="form-list" label="List" hint="Signups land on this list.">
          <select
            id="form-list"
            className={selectClass}
            value={draft.listId ?? ''}
            onChange={(e) => edit({ listId: e.target.value === '' ? null : e.target.value })}
          >
            <option value="">Default signup list</option>
            {publicLists.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id="form-coupon"
          label="Discount code"
          hint="Shown after signup. Leave empty for none."
        >
          <Input
            id="form-coupon"
            value={c.couponCode ?? ''}
            onChange={(e) => config({ couponCode: e.target.value === '' ? null : e.target.value })}
          />
        </Field>
        <Field id="form-headline" label="Headline">
          <Input
            id="form-headline"
            value={c.texts.headline}
            onChange={(e) => text('headline', e.target.value)}
          />
        </Field>
        <Field id="form-button" label="Button text">
          <Input
            id="form-button"
            value={c.texts.buttonText}
            onChange={(e) => text('buttonText', e.target.value)}
          />
        </Field>
        <div className="md:col-span-2">
          <Field id="form-body" label="Text">
            <textarea
              id="form-body"
              rows={3}
              className="w-full rounded-[9px] border border-zinc-300 px-3 py-2 text-sm"
              value={c.texts.body}
              onChange={(e) => text('body', e.target.value)}
            />
          </Field>
        </div>
        <Field id="form-success" label="Thank-you text">
          <Input
            id="form-success"
            value={c.texts.successText}
            onChange={(e) => text('successText', e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-3 gap-2">
          {(
            [
              ['backgroundColor', 'Background'],
              ['textColor', 'Text colour'],
              ['buttonColor', 'Button colour'],
            ] as const
          ).map(([key, label]) => (
            <Field key={key} id={`form-${key}`} label={label}>
              <Input
                id={`form-${key}`}
                value={c.design[key]}
                onChange={(e) => config({ design: { ...c.design, [key]: e.target.value } })}
              />
            </Field>
          ))}
        </div>
      </fieldset>

      <fieldset disabled={readOnly} className="space-y-3">
        <legend className="text-sm font-semibold">Steps</legend>
        <Check
          id="step-multi"
          label="Two steps: email first, then optional details"
          checked={c.steps.multiStep}
          onChange={(v) =>
            config({
              steps: {
                multiStep: v,
                askName: v && c.steps.askName,
                askBirthday: v && c.steps.askBirthday,
              },
            })
          }
        />
        <Check
          id="step-name"
          label="Ask for a first name"
          checked={c.steps.askName}
          disabled={!c.steps.multiStep}
          onChange={(v) => config({ steps: { ...c.steps, askName: v } })}
        />
        <Check
          id="step-birthday"
          label="Ask for a birthday (month and day, with its own consent box)"
          checked={c.steps.askBirthday}
          disabled={!c.steps.multiStep}
          onChange={(v) => config({ steps: { ...c.steps, askBirthday: v } })}
        />
      </fieldset>

      <fieldset disabled={readOnly} className="grid gap-4 md:grid-cols-2">
        <legend className="text-sm font-semibold">When and where it shows</legend>
        <Field id="t-trigger" label="Trigger">
          <select
            id="t-trigger"
            className={selectClass}
            value={t.trigger}
            onChange={(e) => targeting({ trigger: e.target.value as FormTrigger })}
          >
            {TRIGGERS.map((x) => (
              <option key={x.value} value={x.value}>
                {x.label}
              </option>
            ))}
          </select>
        </Field>
        {t.trigger === 'delay' ? (
          <Field id="t-delay" label="Delay (seconds)">
            <Input
              id="t-delay"
              type="number"
              value={Number.isNaN(t.delaySeconds) ? '' : t.delaySeconds}
              onChange={(e) => targeting({ delaySeconds: number(e.target.value) })}
            />
          </Field>
        ) : null}
        {t.trigger === 'scroll' ? (
          <Field id="t-scroll" label="Scroll depth (%)">
            <Input
              id="t-scroll"
              type="number"
              value={Number.isNaN(t.scrollPercent) ? '' : t.scrollPercent}
              onChange={(e) => targeting({ scrollPercent: number(e.target.value) })}
            />
          </Field>
        ) : null}
        <Field
          id="t-frequency"
          label="Show again after (days)"
          hint="After a close. A signup is never asked again."
        >
          <Input
            id="t-frequency"
            type="number"
            value={Number.isNaN(t.frequencyDays) ? '' : t.frequencyDays}
            onChange={(e) => targeting({ frequencyDays: number(e.target.value) })}
          />
        </Field>
        <Field
          id="t-visitor"
          label="Visitors"
          hint="New and returning only work when the shop has cookie consent."
        >
          <select
            id="t-visitor"
            className={selectClass}
            value={t.visitor}
            onChange={(e) => targeting({ visitor: e.target.value as 'all' | 'new' | 'returning' })}
          >
            <option value="all">Everyone</option>
            <option value="new">New visitors</option>
            <option value="returning">Returning visitors</option>
          </select>
        </Field>
        <Field
          id="t-include"
          label="Only on these pages"
          hint="One path per line, like /blog/*. Empty: every page."
        >
          <textarea
            id="t-include"
            rows={3}
            className="w-full rounded-[9px] border border-zinc-300 px-3 py-2 text-sm"
            defaultValue={t.includePaths.join('\n')}
            onChange={(e) => targeting({ includePaths: lines(e.target.value) })}
          />
        </Field>
        <Field id="t-exclude" label="Never on these pages" hint="One path per line.">
          <textarea
            id="t-exclude"
            rows={3}
            className="w-full rounded-[9px] border border-zinc-300 px-3 py-2 text-sm"
            defaultValue={t.excludePaths.join('\n')}
            onChange={(e) => targeting({ excludePaths: lines(e.target.value) })}
          />
        </Field>
        <Field
          id="t-countries"
          label="Countries"
          hint="Two-letter codes, like DK, DE. Empty: every country. Looked up from the visitor's request, not stored."
        >
          <Input
            id="t-countries"
            defaultValue={t.countries.join(', ')}
            onChange={(e) => targeting({ countries: codes(e.target.value, true) })}
          />
        </Field>
        <Field
          id="t-languages"
          label="Page languages"
          hint="Two-letter codes, like da, de. Empty: every language."
        >
          <Input
            id="t-languages"
            defaultValue={t.languages.join(', ')}
            onChange={(e) => targeting({ languages: codes(e.target.value, false) })}
          />
        </Field>
        <Check
          id="t-mobile"
          label="Show on phones"
          checked={t.showOnMobile}
          onChange={(v) => targeting({ showOnMobile: v })}
        />
        <Check
          id="t-desktop"
          label="Show on computers"
          checked={t.showOnDesktop}
          onChange={(v) => targeting({ showOnDesktop: v })}
        />
      </fieldset>

      <fieldset disabled={readOnly} className="space-y-3">
        <legend className="text-sm font-semibold">A/B test</legend>
        <Check
          id="ab-on"
          label="Test a second version (B)"
          checked={c.ab.enabled}
          onChange={(v) => config({ ab: { ...c.ab, enabled: v } })}
        />
        {c.ab.enabled ? (
          <div className="grid gap-4 md:grid-cols-3">
            <Field id="ab-split" label="Share who see B (%)">
              <Input
                id="ab-split"
                type="number"
                value={Number.isNaN(c.ab.splitPercent) ? '' : c.ab.splitPercent}
                onChange={(e) => config({ ab: { ...c.ab, splitPercent: number(e.target.value) } })}
              />
            </Field>
            <Field id="ab-headline" label="B headline" hint="Empty keeps the A text.">
              <Input
                id="ab-headline"
                value={c.ab.variantB.texts.headline ?? ''}
                onChange={(e) =>
                  config({
                    ab: {
                      ...c.ab,
                      variantB: {
                        ...c.ab.variantB,
                        texts: { ...c.ab.variantB.texts, headline: e.target.value },
                      },
                    },
                  })
                }
              />
            </Field>
            <Field id="ab-button" label="B button text" hint="Empty keeps the A text.">
              <Input
                id="ab-button"
                value={c.ab.variantB.texts.buttonText ?? ''}
                onChange={(e) =>
                  config({
                    ab: {
                      ...c.ab,
                      variantB: {
                        ...c.ab.variantB,
                        texts: { ...c.ab.variantB.texts, buttonText: e.target.value },
                      },
                    },
                  })
                }
              />
            </Field>
          </div>
        ) : null}
      </fieldset>

      <fieldset disabled={readOnly} className="space-y-1">
        <legend className="text-sm font-semibold">Numbers</legend>
        <Check
          id="count-views"
          label="Count views"
          checked={draft.countViews}
          onChange={(v) => edit({ countViews: v })}
        />
        <p className="text-xs text-muted">
          Off by default. When on, the page sends one small message per view with the form and the
          variant, nothing about the visitor. Signups are always counted.
        </p>
      </fieldset>

      {problem ? <p className="text-sm text-danger-text">{problem}</p> : null}
      {error ? <Alert>{error}</Alert> : null}
      {saved ? (
        <div role="status" className="text-success-text text-sm">
          Saved.
        </div>
      ) : null}
      {!readOnly ? (
        <Button disabled={problem !== null || save.isPending} onClick={() => save.mutate(draft)}>
          Save
        </Button>
      ) : null}
    </Card>
  );
}

/**
 * Signup forms (K5): every form the tenant has, next to the old sitewide
 * popup, with views, signups and rate per variant. A new form is a draft and
 * shows nothing until an editor sets it live; at most 10 can be live.
 */
export function FormsPage() {
  const { isEditor } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<'new' | string | null>(null);

  const data = useQuery({
    queryKey: ['forms'],
    queryFn: () => apiFetch<SignupFormsResponse>('/forms'),
  });
  const lists = useQuery({
    queryKey: ['lists'],
    queryFn: () => apiFetch<{ items: MailingList[] }>('/lists'),
  });

  const act = useMutation({
    mutationFn: (req: { path: string; method: string; body?: unknown }) =>
      apiFetch<unknown>(req.path, { method: req.method, body: req.body }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['forms'] }),
  });
  const actError =
    act.error instanceof ApiError
      ? (act.error.body.message ?? 'That did not work.')
      : act.isError
        ? 'That did not work.'
        : null;

  function statsFor(id: string, variant: 'a' | 'b'): FormStatRow | undefined {
    return data.data?.stats.find((s) => s.formId === id && s.variant === variant);
  }

  if (data.isPending) return <p className="p-6 text-sm text-muted">Loading forms...</p>;
  if (data.isError) return <Alert className="m-6">Could not load the forms.</Alert>;

  const { forms, popup } = data.data;
  const current =
    editing && editing !== 'new' ? (forms.find((f) => f.id === editing) ?? null) : null;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Forms</h1>
        {isEditor ? <Button onClick={() => setEditing('new')}>New form</Button> : null}
      </div>
      <p className="text-sm text-muted">
        Signup forms for the shop. A new form is a draft; nothing shows until you set it live.
        Numbers are for the last {data.data.days} days.
      </p>
      {actError ? <Alert>{actError}</Alert> : null}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Signup forms</caption>
          <thead>
            <tr className="text-left text-muted">
              <th scope="col" className="p-3">
                Name
              </th>
              <th scope="col" className="p-3">
                Type
              </th>
              <th scope="col" className="p-3">
                Status
              </th>
              <th scope="col" className="p-3">
                Views
              </th>
              <th scope="col" className="p-3">
                Signups
              </th>
              <th scope="col" className="p-3">
                Rate
              </th>
              <th scope="col" className="p-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border">
              <td className="p-3 font-medium">{popup.name}</td>
              <td className="p-3">Popup</td>
              <td className="p-3">{STATUS_LABEL[popup.status]}</td>
              <td className="p-3">-</td>
              <td className="p-3">{statsFor('popup', 'a')?.signups ?? 0}</td>
              <td className="p-3">-</td>
              <td className="p-3">
                <Link className="underline" to="/indstillinger/signup-forms">
                  Edit popup
                </Link>
              </td>
            </tr>
            {forms.map((f) => {
              const a = statsFor(f.id, 'a');
              const b = statsFor(f.id, 'b');
              const rows = f.config.ab.enabled
                ? ([
                    ['A', a],
                    ['B', b],
                  ] as const)
                : ([['', a]] as const);
              return rows.map(([label, row], i) => (
                <tr key={`${f.id}-${label}`} className="border-t border-border">
                  <td className="p-3 font-medium">
                    {i === 0 ? f.name : null}
                    {label ? <span className="ml-2 text-muted">Version {label}</span> : null}
                  </td>
                  <td className="p-3">
                    {i === 0 ? (TYPES.find((x) => x.value === f.type)?.label ?? f.type) : null}
                  </td>
                  <td className="p-3">
                    {i === 0 ? STATUS_LABEL[f.status] : null}
                    {f.config.ab.winner && label.toLowerCase() === f.config.ab.winner ? (
                      <span className="ml-2 text-success-text">Winner</span>
                    ) : null}
                  </td>
                  <td className="p-3">{f.countViews ? (row?.views ?? 0) : '-'}</td>
                  <td className="p-3">{row?.signups ?? 0}</td>
                  <td className="p-3">{f.countViews ? rate(row) : '-'}</td>
                  <td className="space-x-2 p-3 whitespace-nowrap">
                    {i === 0 && isEditor ? (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setEditing(f.id)}
                          aria-label={`Edit ${f.name}`}
                        >
                          Edit
                        </Button>
                        {f.status === 'live' ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            aria-label={`Turn off ${f.name}`}
                            onClick={() =>
                              act.mutate({
                                path: `/forms/${f.id}/status`,
                                method: 'POST',
                                body: { status: 'off' },
                              })
                            }
                          >
                            Turn off
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            aria-label={`Set ${f.name} live`}
                            onClick={() =>
                              act.mutate({
                                path: `/forms/${f.id}/status`,
                                method: 'POST',
                                body: { status: 'live' },
                              })
                            }
                          >
                            Set live
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="danger"
                          aria-label={`Delete ${f.name}`}
                          onClick={() => {
                            if (window.confirm(`Delete ${f.name}? Its numbers are deleted too.`)) {
                              act.mutate({ path: `/forms/${f.id}`, method: 'DELETE' });
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    ) : null}
                    {label && isEditor ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        aria-label={`Pick version ${label} as winner for ${f.name}`}
                        onClick={() =>
                          act.mutate({
                            path: `/forms/${f.id}/winner`,
                            method: 'POST',
                            body: { winner: label.toLowerCase() },
                          })
                        }
                      >
                        Pick {label}
                      </Button>
                    ) : null}
                    {i === 0 && f.config.ab.winner && isEditor ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Run the test again for ${f.name}`}
                        onClick={() =>
                          act.mutate({
                            path: `/forms/${f.id}/winner`,
                            method: 'POST',
                            body: { winner: null },
                          })
                        }
                      >
                        Run again
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ));
            })}
          </tbody>
        </table>
        {forms.length === 0 ? (
          <p className="p-3 text-sm text-muted">No forms yet besides the sitewide popup.</p>
        ) : null}
      </Card>

      {current?.embedCode ? (
        <Card className="space-y-2 p-4">
          <h2 className="text-sm font-semibold">Code for {current.name}</h2>
          <pre className="overflow-x-auto rounded-lg bg-bg p-3 text-xs">{current.embedCode}</pre>
        </Card>
      ) : null}

      {editing ? (
        <Editor
          key={editing}
          initial={
            current
              ? {
                  name: current.name,
                  type: current.type,
                  listId: current.listId,
                  countViews: current.countViews,
                  config: current.config,
                }
              : NEW_FORM
          }
          form={current}
          lists={lists.data?.items ?? []}
          readOnly={!isEditor}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
